import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import { SubHeader } from "@/components/SubHeader";
import { Reveal, SectionTag } from "@/components/Reveal";
import { API, ARTICLE_CATEGORIES } from "@/lib/site";

const CATS = ["All", ...ARTICLE_CATEGORIES];

export default function Knowledge() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    document.title = "Healthcare Knowledge | Healthcare Solutions";
    return () => {
      document.title = "Healthcare Solutions | Connecting Healthcare. Creating Solutions.";
    };
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then(({ data }) => setArticles(data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (cat === "All" ? articles : articles.filter((a) => a.category === cat)),
    [articles, cat]
  );

  return (
    <div data-testid="knowledge-page" className="min-h-screen bg-white">
      <SubHeader crumb="Healthcare Knowledge" />
      <main className="mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-6 lg:px-8">
        <Reveal>
          <SectionTag index="KB" label="Knowledge Hub" />
          <h1 className="font-display mt-6 max-w-3xl text-3xl font-extrabold tracking-tight text-[#0B3C5C] sm:text-4xl lg:text-5xl">
            Healthcare Knowledge. <span className="text-gradient-teal">Simplified.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#4A6B82] sm:text-lg">
            Practical healthcare content created for professionals, hospitals, patients and the
            general public.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2.5">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`knowledge-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setCat(c)}
                className={`rounded-full border px-5 py-2.5 text-xs font-semibold transition-all duration-300 ${
                  cat === c
                    ? "border-[#5FA832] bg-[#5FA832] text-white"
                    : "border-[#0B3C5C]/15 bg-[#0B3C5C]/[0.04] text-[#33586F] hover:border-teal-500/40 hover:text-[#5FA832]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        {loading ? (
          <p className="mt-20 text-center text-sm text-[#6B8AA0]">Loading articles...</p>
        ) : filtered.length === 0 ? (
          <div className="mt-20 flex flex-col items-center gap-3 text-center">
            <BookOpen className="h-10 w-10 text-[#7E9AAC]" />
            <p className="text-sm text-[#4A6B82]">No articles in this category yet. Check back soon.</p>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/knowledge/${a.slug}`}
                  data-testid={`article-card-${a.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#0B3C5C]/10 bg-[#EAF3F8]/60 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-teal-500/30 hover:shadow-xl hover:shadow-teal-900/20"
                >
                  {a.cover_image && (
                    <div className="relative -mx-7 -mt-7 mb-5 h-44 overflow-hidden">
                      <img
                        src={a.cover_image}
                        alt={a.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-transparent to-transparent" />
                    </div>
                  )}
                  <span className="font-mono-x text-[10px] uppercase tracking-[0.25em] text-[#4C8F2F]">
                    {a.category}
                  </span>
                  <h2 className="font-display mt-4 text-lg font-bold leading-snug text-[#0B3C5C] transition-colors group-hover:text-[#5FA832]">
                    {a.title}
                  </h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[#4A6B82]">{a.excerpt}</p>
                  <div className="mt-6 flex items-center justify-between border-t border-[#0B3C5C]/10 pt-4">
                    <span className="text-xs text-[#6B8AA0]">
                      {new Date(a.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#4C8F2F]">
                      Read
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
